import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import showErrorToast, {
  showSuccessToast,
} from "../toast/toast";
import ToggleSwitch from "../Switchery/switchery";
import FormInput from "../Input/formInput";
import CustomModal from "../modal/customModal";
import BranchAssignmentSelector from "../BranchAssignment/BranchAssignmentSelector";
import useThemeColor from "../../../hooks/useThemeColor";
import useDarkMode from "../../../hooks/useDarkMode";
import swal from "sweetalert";

/**
 * Parent-only Manage Branches section for Combo Deals (mirrors item Branch Assignment UX).
 */
const DealBranchAssignmentPanel = ({
  dealId,
  parentCompId,
  currencySymbol = "£",
  dealName = "",
  embedded = false,
}) => {
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    isPriceOverrideActive: false,
    branchDealPriceOverride: "",
    branchPublish: true,
    branchDealOrder: "",
  });

  const load = useCallback(async () => {
    if (!dealId || !parentCompId) return;
    setLoading(true);
    try {
      const assignRes = await axiosInstance.get(
        `${APIs.GET_ASSIGNED_BRANCHES_FOR_DEAL}/${dealId}/${parentCompId}`
      );
      const assigns = (assignRes?.data?.data || []).filter(
        (a) => a.isAssigned !== false && a.IsAssigned !== false
      );
      setAssignments(assigns);
      setSelectedBranches(
        assigns.map((a) => ({
          CompID: a.branchCompID ?? a.BranchCompID,
          CompName:
            a.branchName ??
            a.BranchName ??
            a.compName ??
            a.CompName ??
            `Branch ${a.branchCompID ?? a.BranchCompID}`,
        }))
      );
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Failed to load branch assignments."
      );
    } finally {
      setLoading(false);
    }
  }, [dealId, parentCompId]);

  useEffect(() => {
    load();
  }, [load]);

  const getBranchDisplayName = (branchId) => {
    const fromSelected = selectedBranches.find(
      (b) => Number(b.CompID ?? b.compID) === Number(branchId)
    );
    if (fromSelected) {
      return fromSelected.CompName || fromSelected.compName || `Branch ${branchId}`;
    }
    const fromAssign = assignments.find(
      (a) => Number(a.branchCompID ?? a.BranchCompID) === Number(branchId)
    );
    return (
      fromAssign?.branchName ||
      fromAssign?.BranchName ||
      `Branch ${branchId}`
    );
  };

  const saveBranchAssignments = async () => {
    if (!dealId || !parentCompId) {
      showErrorToast("Branch assignment save is not available.");
      return false;
    }

    const selectedIds = Array.from(
      new Set(
        (selectedBranches || [])
          .map((b) => {
            if (typeof b === "number") return parseInt(b, 10);
            const id = b?.CompID ?? b?.compID ?? b?.branchCompID;
            return id ? parseInt(id, 10) : null;
          })
          .filter((id) => id !== null && !Number.isNaN(id) && id > 0)
      )
    );

    const currentAssignedIds = Array.from(
      new Set(
        (assignments || [])
          .map((a) => {
            const id = a?.branchCompID ?? a?.BranchCompID;
            return id ? parseInt(id, 10) : null;
          })
          .filter((id) => id !== null && !Number.isNaN(id) && id > 0)
      )
    );

    const toAssign = selectedIds.filter((id) => !currentAssignedIds.includes(id));
    const toUnassign = currentAssignedIds.filter((id) => !selectedIds.includes(id));

    if (toAssign.length === 0 && toUnassign.length === 0) {
      showSuccessToast("No branch assignment changes to save.");
      return true;
    }

    const actionText = [
      toAssign.length > 0 ? `${toAssign.length} assign` : null,
      toUnassign.length > 0 ? `${toUnassign.length} unassign` : null,
    ]
      .filter(Boolean)
      .join(", ");

    const confirm = await swal({
      title: "Save branch assignment changes?",
      text: `This will apply: ${actionText}.`,
      icon: "warning",
      buttons: true,
      dangerMode: toUnassign.length > 0,
    });
    if (!confirm) return false;

    try {
      setSaving(true);
      if (toAssign.length > 0) {
        await axiosInstance.post(APIs.POST_ASSIGN_DEAL_TO_BRANCHES, {
          MasterDealID: Number(dealId),
          ParentCompID: Number(parentCompId),
          BranchCompIDs: toAssign,
        });
      }
      if (toUnassign.length > 0) {
        await Promise.all(
          toUnassign.map((branchCompId) =>
            axiosInstance.delete(APIs.DELETE_UNASSIGN_DEAL_FROM_BRANCH, {
              params: {
                masterDealId: dealId,
                parentCompId,
                branchCompId,
              },
            })
          )
        );
      }
      showSuccessToast("Branch assignments saved.");
      await load();
      return true;
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Failed to save branch assignments."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openOverrideModal = (a) => {
    setEditingAssignment(a);
    setOverrideForm({
      isPriceOverrideActive:
        a.isPriceOverrideActive ?? a.IsPriceOverrideActive ?? false,
      branchDealPriceOverride:
        a.branchDealPriceOverride ?? a.BranchDealPriceOverride ?? "",
      branchPublish: a.branchPublish ?? a.BranchPublish ?? true,
      branchDealOrder: a.branchDealOrder ?? a.BranchDealOrder ?? "",
    });
    setOverrideModalOpen(true);
  };

  const closeOverrideModal = () => {
    setOverrideModalOpen(false);
    setEditingAssignment(null);
  };

  const saveOverride = async () => {
    if (!editingAssignment) return;
    try {
      const price =
        overrideForm.branchDealPriceOverride === ""
          ? null
          : parseFloat(overrideForm.branchDealPriceOverride);
      await axiosInstance.put(APIs.PUT_UPDATE_BRANCH_DEAL_ASSIGNMENT, {
        ID: editingAssignment.id ?? editingAssignment.ID,
        MasterCompID: Number(parentCompId),
        BranchCompID:
          editingAssignment.branchCompID ?? editingAssignment.BranchCompID,
        IsPriceOverrideActive: !!overrideForm.isPriceOverrideActive,
        BranchDealPriceOverride: Number.isFinite(price) ? price : null,
        ClearBranchDealPriceOverride:
          !overrideForm.isPriceOverrideActive || price == null,
        BranchPublish: overrideForm.branchPublish,
        BranchDealOrder:
          overrideForm.branchDealOrder === ""
            ? null
            : parseInt(overrideForm.branchDealOrder, 10),
        ClearBranchDealOrder: overrideForm.branchDealOrder === "",
      });
      showSuccessToast("Branch override saved.");
      closeOverrideModal();
      await load();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Failed to update override."
      );
    }
  };

  const resetOverride = async (a) => {
    const confirm = await swal({
      title: "Reset branch overrides?",
      text: "Price override, publish, and order will revert to master deal defaults.",
      icon: "warning",
      buttons: true,
    });
    if (!confirm) return;
    try {
      await axiosInstance.put(APIs.PUT_UPDATE_BRANCH_DEAL_ASSIGNMENT, {
        ID: a.id ?? a.ID,
        MasterCompID: Number(parentCompId),
        BranchCompID: a.branchCompID ?? a.BranchCompID,
        IsPriceOverrideActive: false,
        BranchDealPriceOverride: null,
        ClearBranchDealPriceOverride: true,
        BranchPublish: true,
        BranchDealOrder: null,
        ClearBranchDealOrder: true,
      });
      showSuccessToast("Branch overrides reset.");
      await load();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Failed to reset overrides."
      );
    }
  };

  const muted = isDarkMode ? "#b2bec3" : "#6c757d";
  const surface = isDarkMode ? "#1e2124" : "#f8f9fa";
  const cardBg = isDarkMode ? "#2d3436" : "#ffffff";

  const body = (
    <>
      {loading ? (
        <div className="text-muted py-3">Loading branch assignments...</div>
      ) : (
        <>
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h6 style={{ marginBottom: 5, fontWeight: 600 }}>
                Assign to Branches
              </h6>
              <p style={{ margin: 0, color: muted, fontSize: 14 }}>
                Select which branches should have access to this deal
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowAssignModal(true)}
              disabled={saving}
              style={{
                backgroundColor: themeColor,
                border: "none",
                borderRadius: 8,
                padding: "8px 16px",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <i className="fas fa-edit me-2" />
              Manage Branches
            </button>
          </div>

          {selectedBranches.length > 0 ? (
            <div
              style={{
                padding: 15,
                backgroundColor: surface,
                borderRadius: 8,
                border: `1px solid ${themeColor}30`,
              }}
            >
              <div className="d-flex align-items-center mb-2">
                <i
                  className="fas fa-check-circle me-2"
                  style={{ color: themeColor }}
                />
                <span style={{ fontWeight: 500 }}>
                  Assigned to {selectedBranches.length} branch
                  {selectedBranches.length !== 1 ? "es" : ""}
                </span>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {selectedBranches.map((branch) => {
                  const branchId =
                    typeof branch === "number"
                      ? branch
                      : branch.CompID || branch.compID;
                  const branchName =
                    typeof branch === "object"
                      ? branch.CompName ||
                        branch.compName ||
                        getBranchDisplayName(branchId)
                      : `Branch ${branch}`;
                  return (
                    <span
                      key={branchId}
                      className="badge"
                      style={{
                        backgroundColor: `${themeColor}20`,
                        color: themeColor,
                        padding: "6px 12px",
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      {branchName}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: 15,
                backgroundColor: surface,
                borderRadius: 8,
                border: `1px dashed ${themeColor}50`,
                textAlign: "center",
                color: muted,
              }}
            >
              <i className="fas fa-info-circle me-2" />
              No branches assigned yet. Click &quot;Manage Branches&quot; to
              assign this deal to child branches.
            </div>
          )}

          <div
            style={{
              height: 1,
              backgroundColor: isDarkMode ? "#404040" : "#e9ecef",
              margin: "22px 0",
            }}
          />

          <div className="mb-3">
            <h6 style={{ marginBottom: 5, fontWeight: 600 }}>
              Branch Overrides
            </h6>
            <p style={{ margin: 0, color: muted, fontSize: 14 }}>
              Review and manage price, publish, and order per branch
            </p>
          </div>

          {assignments.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th className="text-center">Publish</th>
                    <th className="text-center">Price Override</th>
                    <th className="text-center">Deal Price</th>
                    <th className="text-center">Order</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => {
                    const id = a.id ?? a.ID;
                    const branchId = a.branchCompID ?? a.BranchCompID;
                    const name =
                      a.branchName ??
                      a.BranchName ??
                      getBranchDisplayName(branchId);
                    const overrideOn = !!(
                      a.isPriceOverrideActive ?? a.IsPriceOverrideActive
                    );
                    const overridePrice =
                      a.branchDealPriceOverride ?? a.BranchDealPriceOverride;
                    const publish = a.branchPublish ?? a.BranchPublish ?? true;
                    const order = a.branchDealOrder ?? a.BranchDealOrder;
                    return (
                      <tr key={id}>
                        <td className="align-middle">{name}</td>
                        <td className="align-middle text-center">
                          {publish ? (
                            <span className="badge bg-success">On</span>
                          ) : (
                            <span className="badge bg-secondary">Off</span>
                          )}
                        </td>
                        <td className="align-middle text-center">
                          {overrideOn ? "Enabled" : "Disabled"}
                        </td>
                        <td className="align-middle text-center">
                          {overrideOn && overridePrice != null
                            ? `${currencySymbol}${Number(overridePrice).toFixed(2)}`
                            : "Inherit"}
                        </td>
                        <td className="align-middle text-center">
                          {order != null && order !== "" ? order : "Inherit"}
                        </td>
                        <td className="align-middle text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              style={{
                                borderColor: themeColor,
                                color: themeColor,
                              }}
                              onClick={() => openOverrideModal(a)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => resetOverride(a)}
                            >
                              Reset
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{
                padding: 15,
                backgroundColor: surface,
                borderRadius: 8,
                border: `1px dashed ${themeColor}50`,
                textAlign: "center",
                color: muted,
              }}
            >
              <i className="fas fa-info-circle me-2" />
              Assign branches first to manage overrides.
            </div>
          )}
        </>
      )}

      <BranchAssignmentSelector
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        selectedBranches={selectedBranches}
        setSelectedBranches={setSelectedBranches}
        productId={null}
        parentCompID={parentCompId}
        onSubmitSelection={saveBranchAssignments}
      />

      <CustomModal
        title="Branch Overrides"
        subtitle="Update price, publish, and order for this branch."
        isOpen={overrideModalOpen}
        onClose={closeOverrideModal}
        isCentered
        showButton
        onSubmit={saveOverride}
        submitButtonText="Save"
        modalSize="modal-md"
      >
        <div style={{ padding: 20 }}>
          <div
            className="mb-4"
            style={{
              padding: 14,
              borderRadius: 14,
              backgroundColor: surface,
              border: `1px solid ${themeColor}20`,
            }}
          >
            <div className="fw-bold" style={{ fontSize: 14 }}>
              Branch
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
              {editingAssignment?.branchName ||
                editingAssignment?.BranchName ||
                `Branch ${
                  editingAssignment?.branchCompID ??
                  editingAssignment?.BranchCompID ??
                  ""
                }`}
            </div>
            {dealName ? (
              <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Deal: {dealName}
              </div>
            ) : null}
          </div>

          <ToggleSwitch
            label="Enable price override"
            isChecked={overrideForm.isPriceOverrideActive}
            onToggle={(v) =>
              setOverrideForm((f) => ({ ...f, isPriceOverrideActive: v }))
            }
            themeColor={themeColor}
          />
          {overrideForm.isPriceOverrideActive && (
            <FormInput
              label={`Branch deal price (${currencySymbol})`}
              type="number"
              value={overrideForm.branchDealPriceOverride}
              onchange={(e) =>
                setOverrideForm((f) => ({
                  ...f,
                  branchDealPriceOverride: e.target.value,
                }))
              }
            />
          )}
          <ToggleSwitch
            label="Publish on branch POS"
            isChecked={!!overrideForm.branchPublish}
            onToggle={(v) =>
              setOverrideForm((f) => ({ ...f, branchPublish: v }))
            }
            themeColor={themeColor}
          />
          <FormInput
            label="Branch deal order (leave empty to inherit)"
            type="number"
            value={overrideForm.branchDealOrder}
            onchange={(e) =>
              setOverrideForm((f) => ({
                ...f,
                branchDealOrder: e.target.value,
              }))
            }
          />
        </div>
      </CustomModal>
    </>
  );

  if (embedded) {
    return <div>{body}</div>;
  }

  return (
    <div className="row g-4 mb-4 mx-0">
      <div className="col-12 px-0">
        <div
          className="card border-0 shadow-sm"
          style={{
            backgroundColor: cardBg,
            border: isDarkMode ? "1px solid #404040" : undefined,
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          <div
            className="card-header border-0"
            style={{
              backgroundColor: themeColor,
              color: "white",
              padding: "15px 20px",
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            <i className="fas fa-code-branch me-2" />
            Branch Assignment
          </div>
          <div className="card-body" style={{ padding: 25 }}>
            {body}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealBranchAssignmentPanel;
